import { AlertTriangle, XCircle, Loader2 } from 'lucide-react'
import { Badge } from '@/components/StatusBadge'
import type { BadgeTone } from '@/components/StatusBadge'
import { cn } from '@/lib/utils'

export type SyncHistoryFilter = 'all' | 'warn' | 'error'

export type SyncHistoryRow = {
  id: number
  startedAt: string
  profileLabel: string
  syncType: string
  duration: string
  entitiesSynced: number
  status: 'running' | 'completed' | 'failed'
  hasErrors: boolean
  errorMessage: string | null
}

function statusBadge(row: SyncHistoryRow): { tone: BadgeTone; label: string } {
  if (row.status === 'failed') return { tone: 'bad', label: 'Fehler' }
  if (row.status === 'running') return { tone: 'info', label: 'Läuft' }
  if (row.hasErrors) return { tone: 'warn', label: 'Warnung' }
  return { tone: 'good', label: 'OK' }
}

type Props = {
  rows: SyncHistoryRow[]
  filter: SyncHistoryFilter
  onFilterChange: (next: SyncHistoryFilter) => void
  totalCount: number
  warnCount: number
  errorCount: number
}

export default function SyncRunHistoryTable({
  rows,
  filter,
  onFilterChange,
  totalCount,
  warnCount,
  errorCount,
}: Props) {
  const pills: Array<{ k: SyncHistoryFilter; label: string; count: number }> = [
    { k: 'all', label: 'Alle', count: totalCount },
    { k: 'warn', label: 'Warnungen', count: warnCount },
    { k: 'error', label: 'Fehler', count: errorCount },
  ]

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5 border-b border-[var(--border)] px-4 py-3">
        {pills.map((p) => {
          const active = filter === p.k
          return (
            <button
              key={p.k}
              type="button"
              onClick={() => onFilterChange(p.k)}
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11.5px] font-semibold transition-colors',
                active
                  ? 'border-zinc-900 bg-zinc-900 text-white'
                  : 'border-[var(--border)] bg-[var(--card)] text-[var(--foreground)] hover:bg-[var(--muted)]',
              )}
            >
              {p.label}{' '}
              <span className={cn('font-medium', active ? 'text-white/70' : 'text-[var(--muted-foreground)]')}>
                {p.count}
              </span>
            </button>
          )
        })}
      </div>

      {rows.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-[var(--muted-foreground)]">
          Keine Einträge im Filter „{pills.find((p) => p.k === filter)?.label}".
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="bg-[var(--muted)]/40 border-b border-[var(--border)] text-left">
                {['Run', 'Zeit', 'Profil', 'Typ', 'Dauer', 'Entities', 'Status', 'Fehler / Hinweis'].map(
                  (h, i) => (
                    <th
                      key={h}
                      className={cn(
                        'px-3.5 py-2.5 text-[10.5px] font-bold uppercase tracking-wider text-[var(--muted-foreground)]',
                        (i === 4 || i === 5) && 'text-right',
                      )}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const badge = statusBadge(row)
                const rowBg =
                  row.status === 'failed'
                    ? 'bg-red-50/40'
                    : row.hasErrors
                      ? 'bg-amber-50/40'
                      : ''
                return (
                  <tr
                    key={`${row.id}-${row.profileLabel}-${row.startedAt}`}
                    className={cn('border-b border-[var(--border)] last:border-b-0', rowBg)}
                  >
                    <td className="px-3.5 py-2.5 font-mono text-[11px] text-[var(--muted-foreground)]">
                      #{row.id}
                    </td>
                    <td className="px-3.5 py-2.5 tabular-nums text-[var(--foreground)]">
                      {row.startedAt}
                    </td>
                    <td className="px-3.5 py-2.5 font-medium text-[var(--foreground)]">
                      {row.profileLabel}
                    </td>
                    <td className="px-3.5 py-2.5 text-[var(--muted-foreground)]">{row.syncType}</td>
                    <td className="px-3.5 py-2.5 text-right tabular-nums text-[var(--muted-foreground)]">
                      {row.duration}
                    </td>
                    <td className="px-3.5 py-2.5 text-right tabular-nums text-[var(--foreground)]">
                      {row.entitiesSynced.toLocaleString('de-DE')}
                    </td>
                    <td className="px-3.5 py-2.5">
                      <Badge tone={badge.tone} size="sm">
                        {badge.label}
                      </Badge>
                    </td>
                    <td
                      className={cn(
                        'max-w-[460px] px-3.5 py-2.5 text-[12px]',
                        row.status === 'failed'
                          ? 'text-red-700'
                          : row.hasErrors
                            ? 'text-amber-700'
                            : 'text-[var(--muted-foreground)]',
                      )}
                    >
                      {row.errorMessage ? (
                        <span className="inline-flex items-start gap-1.5">
                          {row.status === 'failed' ? (
                            <XCircle className="mt-px h-3.5 w-3.5 shrink-0 text-red-500" />
                          ) : row.status === 'running' ? (
                            <Loader2 className="mt-px h-3.5 w-3.5 shrink-0 animate-spin text-blue-500" />
                          ) : (
                            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0 text-amber-500" />
                          )}
                          <span>{row.errorMessage}</span>
                        </span>
                      ) : (
                        <span className="text-[var(--muted-foreground)]">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
